import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { normalizeApiError } from '../../api/apiError';
import { searchEmployeeLeaveBalances } from '../../services/leaveBalanceAdminService';
import type {
  LeaveBalanceEmployeeSummary,
  LeaveBalanceTypeOption,
} from '../../types/leaveBalanceAdmin';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { formatLeaveBalanceValue } from '../../utils/leaveSession';
import { leaveBalanceDisplayColor } from '../../utils/leaveBalance';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'LeaveBalanceList'>;

export function LeaveBalanceListScreen({ navigation }: Props) {
  const palette = useAppColors();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [search, setSearch] = useState('');
  const [leaveTypeId, setLeaveTypeId] = useState<number | null>(null);
  const [leaveTypes, setLeaveTypes] = useState<LeaveBalanceTypeOption[]>([]);
  const [items, setItems] = useState<LeaveBalanceEmployeeSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const yearRef = useRef(year);
  const searchRef = useRef(search);
  const leaveTypeIdRef = useRef(leaveTypeId);
  yearRef.current = year;
  searchRef.current = search;
  leaveTypeIdRef.current = leaveTypeId;

  const load = useCallback(async (options?: {
    refresh?: boolean;
    page?: number;
    leaveTypeId?: number | null;
  }) => {
    const leaveYear = Number(yearRef.current);
    if (!Number.isInteger(leaveYear) || leaveYear < 2000 || leaveYear > 2200) {
      setError('Enter a valid leave year.');
      setLoading(false);
      return;
    }
    const selectedType =
      options && 'leaveTypeId' in options ? options.leaveTypeId ?? null : leaveTypeIdRef.current;
    const nextPage = options?.page ?? 1;
    if (options?.refresh) setRefreshing(true);
    else if (nextPage > 1) setLoadingMore(true);
    else setLoading(true);
    try {
      const result = await searchEmployeeLeaveBalances({
        search: searchRef.current,
        leaveTypeId: selectedType,
        leaveYear,
        page: nextPage,
      });
      setLeaveTypes(result.leaveTypes);
      setItems(current => (nextPage === 1 ? result.items : [...current, ...result.items]));
      setPage(result.pagination.page);
      setTotalPages(result.pagination.totalPages);
      setError(null);
    } catch (caught) {
      setError(normalizeApiError(caught).message || 'Unable to load leave balances.');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }, []);

  useFocusEffect(useCallback(() => {
    load().catch(() => undefined);
  }, [load]));

  const themed = useMemo(
    () => ({
      chip: {
        borderWidth: 1,
        borderColor: palette.border,
        borderRadius: 999,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        backgroundColor: palette.surface,
      },
      chipActive: {
        borderWidth: 1,
        borderColor: palette.primary,
        borderRadius: 999,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        backgroundColor: palette.surface,
      },
      chipText: { ...typography.caption, color: palette.textPrimary },
      chipTextActive: { ...typography.caption, color: palette.primary, fontWeight: '700' as const },
    }),
    [palette],
  );

  return (
    <PermissionGuard permission={PERMISSIONS.SETTINGS_VIEW} fallback={<UnauthorizedScreen />}>
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader title="Leave balances" onBack={navigation.goBack} />
        <View style={styles.body}>
          <AppInput label="Year" value={year} onChangeText={setYear} keyboardType="number-pad" />
          <AppInput
            label="Search"
            value={search}
            onChangeText={setSearch}
            placeholder="Name or employee number"
            autoCapitalize="none"
          />
          <AppButton title="Search" variant="secondary" onPress={() => load()} />
          {leaveTypes.length ? (
            <View style={styles.chips}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="All leave types"
                onPress={() => {
                  setLeaveTypeId(null);
                  load({ leaveTypeId: null }).catch(() => undefined);
                }}
                style={leaveTypeId == null ? themed.chipActive : themed.chip}
              >
                <Text style={leaveTypeId == null ? themed.chipTextActive : themed.chipText}>All types</Text>
              </Pressable>
              {leaveTypes.map(type => {
                const active = leaveTypeId === type.leaveTypeId;
                return (
                  <Pressable
                    key={type.leaveTypeId}
                    accessibilityRole="button"
                    accessibilityLabel={formatDisplayLabel(type.leaveTypeName)}
                    onPress={() => {
                      setLeaveTypeId(type.leaveTypeId);
                      load({ leaveTypeId: type.leaveTypeId }).catch(() => undefined);
                    }}
                    style={active ? themed.chipActive : themed.chip}
                  >
                    <Text style={active ? themed.chipTextActive : themed.chipText}>
                      {formatDisplayLabel(type.leaveTypeName)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          ) : null}
          {error && items.length === 0 ? (
            <ErrorState message={error} onRetry={() => load()} />
          ) : (
            <FlatList
              data={items}
              keyExtractor={item => String(item.employeeId)}
              refreshControl={
                <RefreshControl refreshing={refreshing} onRefresh={() => load({ refresh: true })} />
              }
              contentContainerStyle={styles.list}
              onEndReached={() => {
                if (!loading && !loadingMore && page < totalPages) load({ page: page + 1 });
              }}
              ListEmptyComponent={
                !loading ? (
                  <EmptyState
                    title="No employee balances"
                    message="No employees matched this search for the selected year."
                  />
                ) : null
              }
              ListFooterComponent={
                loadingMore ? (
                  <View style={styles.footer}>
                    <ActivityIndicator color={palette.primary} />
                  </View>
                ) : null
              }
              renderItem={({ item }) => (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`${item.displayName} leave balances`}
                  onPress={() =>
                    navigation.navigate(routes.leaveBalanceEmployee, {
                      employeeId: item.employeeId,
                      leaveYear: Number(year) || undefined,
                    })
                  }
                >
                  <AppCard style={styles.card}>
                    <Text style={[styles.title, { color: palette.textPrimary }]}>{item.displayName}</Text>
                    <Text style={[styles.meta, { color: palette.textSecondary }]}>
                      {[item.employeeNumber, item.departmentName, item.designationName]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                    {item.balances.length ? (
                      item.balances.map(balance => (
                        <Text
                          key={`${balance.leaveTypeId}-${balance.leaveYear}`}
                          style={[
                            styles.days,
                            {
                              color: leaveBalanceDisplayColor(
                                balance.availableBalance,
                                palette.primary,
                                palette.error,
                              ),
                            },
                          ]}
                        >
                          {formatDisplayLabel(balance.leaveTypeName)}: {balance.isLossOfPay ? `${balance.daysTaken ?? 0} day(s) taken · no balance` : formatLeaveBalanceValue(balance.availableBalance)}
                          {balance.pendingLeaveDays
                            ? ` · pending ${formatLeaveBalanceValue(balance.pendingLeaveDays)}`
                            : ''}
                        </Text>
                      ))
                    ) : (
                      <Text style={[styles.meta, { color: palette.textSecondary }]}>
                        No leave balance rows for this year yet.
                      </Text>
                    )}
                  </AppCard>
                </Pressable>
              )}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg, gap: spacing.sm },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  list: { flexGrow: 1, gap: spacing.sm, paddingBottom: spacing.xl },
  card: { gap: spacing.xs },
  title: typography.sectionTitle,
  meta: typography.body,
  days: { ...typography.body, fontWeight: '700' },
  footer: { paddingVertical: spacing.md },
});
